import { Dayjs } from "dayjs";
import { MdaFilterOption, RiskRating, ReviewStatus } from "./mda-types";

export const INCIDENT_DATE_FILTER_KEY = "IncidentDate";

export const matchesItem = (selectedValue: string, itemValue: string) =>
  selectedValue.toLowerCase() === itemValue.toLowerCase();

export const getFilterItems = (filters: MdaFilterOption[], key: string) =>
  [...(filters.find((filter) => filter.key === key)?.items ?? [])].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );

export const toTitleCaseRiskRating = (value: string): RiskRating | null => {
  const rating = value.toLowerCase();
  if (rating === "high") return "High";
  if (rating === "medium") return "Medium";
  if (rating === "low") return "Low";
  return null;
};

export const toReviewStatus = (value: string): ReviewStatus | null => {
  const status = value.toLowerCase();
  if (status === "read") return "read";
  if (status === "unread") return "unread";
  return null;
};

export const getSelectedClaimStatuses = (filters: MdaFilterOption[]) =>
  getFilterItems(filters, "Status").map(({ value }) => value);

export const getSelectedRiskRatings = (filters: MdaFilterOption[]) =>
  getFilterItems(filters, "RiskRating")
    .filter(({ selected }) => selected)
    .map(({ description, value }) =>
      toTitleCaseRiskRating(description || value),
    )
    .filter((rating): rating is RiskRating => Boolean(rating));

export const getSelectedReviewStatus = (
  filters: MdaFilterOption[],
): ReviewStatus => {
  const selectedStatuses = getFilterItems(filters, "ReviewStatus")
    .filter(({ selected }) => selected)
    .map(({ description, value }) => toReviewStatus(value || description))
    .filter((status): status is ReviewStatus => Boolean(status));

  if (selectedStatuses.length > 1) return "all";

  return selectedStatuses[0] ?? "";
};

export const buildAppliedFilters = (
  filters: MdaFilterOption[],
  selectedRiskRatings: RiskRating[],
  selectedReviewStatus: ReviewStatus,
  selectedClaimStatuses: string[],
  selectedIncidentDateFrom: Dayjs | null,
  selectedIncidentDateTo: Dayjs | null,
) =>
  filters.map((filter) => {
    if (filter.key === INCIDENT_DATE_FILTER_KEY) {
      return {
        ...filter,
        range: {
          from: selectedIncidentDateFrom
            ? selectedIncidentDateFrom.toISOString()
            : null,
          to: selectedIncidentDateTo
            ? selectedIncidentDateTo.toISOString()
            : null,
        },
      };
    }

    return {
      ...filter,
      items: filter.items.map((item) => {
        const { selected: initialSelected } = item;
        let selected = initialSelected;

        if (filter.key === "Status") {
          selected = selectedClaimStatuses.includes(item.value);
        }
        if (filter.key === "RiskRating") {
          selected = selectedRiskRatings.some(
            (rating) =>
              matchesItem(rating, item.value) ||
              matchesItem(rating, item.description),
          );
        }
        if (filter.key === "ReviewStatus") {
          selected =
            selectedReviewStatus === "all" ||
            matchesItem(selectedReviewStatus, item.value) ||
            matchesItem(selectedReviewStatus, item.description);
        }

        return { ...item, selected };
      }),
    };
  });
